import { useState, useEffect } from 'react';

/**
 * Custom hook to manage table settings (pagination, sorting) that persist between navigation
 * @param {string} tableId - Unique identifier for the table
 * @param {Object} defaultSettings - Default settings for the table
 * @returns {Object} Table settings and setter functions
 */
export default function useTableSettings(tableId, defaultSettings = {}) {
  // Set default values
  const defaults = {
    page: 0,
    rowsPerPage: 25,
    orderBy: '',
    order: 'asc',
    ...defaultSettings
  };

  // Initialize state from session storage or defaults
  const [settings, setSettings] = useState(() => {
    try {
      const stored = sessionStorage.getItem(`table-settings-${tableId}`);
      return stored ? JSON.parse(stored) : defaults;
    } catch (error) {
      console.error('Error retrieving table settings from session storage:', error);
      return defaults;
    }
  });

  // Save settings to session storage when they change
  useEffect(() => {
    try {
      sessionStorage.setItem(`table-settings-${tableId}`, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving table settings to session storage:', error);
    }
  }, [settings, tableId]);

  // Convenience setter functions
  const updateSettings = (newSettings) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      ...newSettings
    }));
  };

  const setPage = (page) => {
    updateSettings({ page });
  };

  const setRowsPerPage = (rowsPerPage) => {
    updateSettings({ rowsPerPage, page: 0 }); // Reset to first page when changing rows per page
  };

  const setSortOrder = (orderBy, order) => {
    updateSettings({ orderBy, order });
  };

  return {
    ...settings,
    setPage,
    setRowsPerPage,
    setSortOrder,
    updateSettings
  };
} 